import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { formatUnit, formatAmount, formatUnitPlural } from "@/i18n/units";
import { plural } from "../i18n/plural";
import { useTutorial } from "@/contexts/TutorialContext";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Refrigerator, Plus, X, AlertTriangle, Loader2,
  Trash2, Check, Crown, Package, ChevronRight, Calendar, Search, Home,
  CheckCircle2, Circle, ListChecks
} from "lucide-react";
import TutorialTooltip from "@/components/TutorialTooltip";
import { toLocalDateStr } from "@/lib/utils";

export default function FridgePage() {
  const { api } = useAuth();
  const { t, lang, onLangChange } = useLanguage();
  const { tutorialActive, tutorialStep, updateAppState, goToStep } = useTutorial();
  const navigate = useNavigate();
  const location = useLocation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pendingItems, setPendingItems] = useState([]);
  const [hasPending, setHasPending] = useState(false);
  const [isPremium, setIsPremium] = useState(false);
  const [showPending, setShowPending] = useState(false);
  const [confirmingPending, setConfirmingPending] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addName, setAddName] = useState("");
  const [addCategory, setAddCategory] = useState("other");
  const [addQty, setAddQty] = useState("");
  const [addUnit, setAddUnit] = useState("шт");
  const [addExpiry, setAddExpiry] = useState("");
  const [addingNew, setAddingNew] = useState(false);
  const [expiryFilter, setExpiryFilter] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [activeCat, setActiveCat] = useState("all");
  const [addStep, setAddStep] = useState("search"); // "search" | "details"
  const [addSuggestions, setAddSuggestions] = useState([]);
  const [addSearch, setAddSearch] = useState("");
  const [showGoHomeStep, setShowGoHomeStep] = useState(false);
  // Bulk selection + clear-all
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [clearing, setClearing] = useState(false);

  // Categories matching products.json database
  const CATEGORIES = [
    "dairy_eggs", "meat", "deli_meat", "seafood", "vegetables", "mushrooms",
    "fruits_berries", "grains_bakery", "nuts_seeds", "spices_oils",
    "sweets_snacks", "frozen_foods", "beverages", "beverages_alcohol", "other"
  ];
  const CAT_ICONS = {
    dairy_eggs: "🥛", meat: "🥩", deli_meat: "🌭", seafood: "🐟",
    vegetables: "🥕", mushrooms: "🍄", fruits_berries: "🍎", grains_bakery: "🍞",
    nuts_seeds: "🥜", spices_oils: "🧂", sweets_snacks: "🍫", frozen_foods: "❄️",
    beverages: "🥤", beverages_alcohol: "🍷", other: "📦",
  };

  // Update tutorial state
  useEffect(() => {
    updateAppState({ currentPage: location.pathname });
  }, [location.pathname, updateAppState]);

  const fetchItems = useCallback(async () => {
    try {
      const res = await api().get("/fridge");
      setItems(res.data || []);
      // Update tutorial app state
      updateAppState({ hasFridgeItems: (res.data || []).length > 0 });
    } catch (err) { console.error(err); }
    setLoading(false);
  }, [api, updateAppState]);

  const fetchPending = useCallback(async () => {
    try {
      const res = await api().get("/fridge/pending");
      setPendingItems(res.data?.items || []);
      const hasPendingItems = (res.data?.items || []).length > 0;
      setHasPending(hasPendingItems);
      setIsPremium(res.data?.is_premium || false);
      // Update tutorial app state
      updateAppState({ hasPendingFridgeItems: hasPendingItems });
    } catch (err) { console.error(err); }
  }, [api, updateAppState]);

  useEffect(() => { fetchItems(); fetchPending(); }, [fetchItems, fetchPending]);

  // Re-fetch fridge when UI language changes so item names re-localize
  useEffect(() => {
    return onLangChange(() => { fetchItems(); fetchPending(); });
  }, [onLangChange, fetchItems, fetchPending]);

  const today = toLocalDateStr();
  const daysUntilExpiry = (expiryDate) => {
    if (!expiryDate) return null;
    return Math.ceil((new Date(expiryDate) - new Date(today)) / 86400000);
  };

  const expiringItems = items.filter((i) => {
    const d = daysUntilExpiry(i.expiry_date);
    return d !== null && d < 4;
  });

  // Already past their date. A subset of expiringItems (d < 0 is also < 4), so
  // this gets a line inside the existing banner rather than a second banner.
  const expiredItems = items.filter((i) => {
    const d = daysUntilExpiry(i.expiry_date);
    return d !== null && d < 0;
  });

  const baseItems = expiryFilter ? expiringItems : items;

  // Group items by category
  const grouped = useMemo(() => {
    const map = {};
    baseItems.forEach((item) => {
      const cat = item.category || "other";
      if (!map[cat]) map[cat] = [];
      map[cat].push(item);
    });
    return map;
  }, [baseItems]);

  // Categories that actually have items (preserving order)
  const populatedCats = useMemo(() => CATEGORIES.filter((c) => grouped[c]?.length > 0), [grouped]);

  // Filtered display
  const displayCats = useMemo(
    () => (activeCat === "all" ? populatedCats : (grouped[activeCat] ? [activeCat] : [])),
    [activeCat, populatedCats, grouped]
  );

  // Purchase pending → fridge
  const updatePendingExpiry = async (itemId, expiry) => {
    try {
      await api().put(`/fridge/pending/${itemId}`, { expiry_date: expiry });
      setPendingItems((prev) =>
        prev.map((i) => (i.id === itemId ? { ...i, expiry_date: expiry } : i))
      );
    } catch (err) { console.error(err); }
  };

  const confirmPending = async () => {
    setConfirmingPending(true);
    try {
      await api().post("/fridge/pending/confirm", { items: pendingItems });
      setPendingItems([]);
      setHasPending(false);
      setShowPending(false);
      await fetchItems();
      // Update tutorial state - pending items confirmed
      updateAppState({ hasPendingFridgeItems: false, hasFridgeItems: true });
      // Advance tutorial Step 7 -> Step 8 (go to home)
      if (tutorialActive && tutorialStep === 7) {
        goToStep(8);
        setShowGoHomeStep(true);
      }
    } catch (err) { console.error(err); }
    setConfirmingPending(false);
  };

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      await api().delete(`/fridge/${id}`);
      setItems((prev) => prev.filter((i) => i.id !== id));
      if (showDetail?.id === id) setShowDetail(null);
    } catch (err) { console.error(err); }
    setDeletingId(null);
  };

  // ----- Bulk selection + clear-all -----
  const enterSelectMode = () => { setSelectMode(true); setSelectedIds([]); };
  const exitSelectMode = () => { setSelectMode(false); setSelectedIds([]); };

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  // Items currently visible under the active category / expiry filter
  const visibleItems = useMemo(
    () => displayCats.flatMap((cat) => grouped[cat] || []),
    [displayCats, grouped]
  );
  const allVisibleSelected =
    visibleItems.length > 0 && visibleItems.every((i) => selectedIds.includes(i.id));

  const toggleSelectAll = () => {
    if (allVisibleSelected) {
      const visibleIds = new Set(visibleItems.map((i) => i.id));
      setSelectedIds((prev) => prev.filter((id) => !visibleIds.has(id)));
    } else {
      setSelectedIds((prev) => Array.from(new Set([...prev, ...visibleItems.map((i) => i.id)])));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    setBulkDeleting(true);
    try {
      await api().post("/fridge/bulk-delete", { ids: selectedIds });
      setItems((prev) => prev.filter((i) => !selectedIds.includes(i.id)));
      exitSelectMode();
    } catch (err) { console.error(err); }
    setBulkDeleting(false);
  };

  const handleClearAll = async () => {
    setClearing(true);
    try {
      await api().delete("/fridge");
      setItems([]);
      setShowClearConfirm(false);
      exitSelectMode();
      updateAppState({ hasFridgeItems: false });
    } catch (err) { console.error(err); }
    setClearing(false);
  };

  const openDetail = (item) => {
    setShowDetail(item);
    setEditData({
      name: item.name || "",
      quantity: item.quantity || "",
      unit: item.unit || "",
      expiry_date: item.expiry_date || "",
      category: item.category || "other",
    });
  };

  const saveDetail = async () => {
    if (!showDetail) return;
    setSaving(true);
    try {
      await api().put(`/fridge/${showDetail.id}`, editData);
      await fetchItems();
      setShowDetail(null);
    } catch (err) { console.error(err); }
    setSaving(false);
  };

  const handleAddItem = async () => {
    if (!addName.trim()) return;
    setAddingNew(true);
    // Quantity must always be set (presence-bug fix): empty -> default by unit.
    // Backend applies the same default; this keeps the UI payload consistent.
    const QTY_DEFAULTS = { "шт": "1", "кг": "1", "г": "200", "л": "1" };
    const finalQty = addQty.trim() === "" ? (QTY_DEFAULTS[addUnit] || "1") : addQty;
    try {
      await api().post("/fridge", {
        name: addName.trim(), category: addCategory,
        quantity: finalQty, unit: addUnit, expiry_date: addExpiry || null,
      });
      await fetchItems();
      setShowAddModal(false);
      setAddName(""); setAddQty(""); setAddExpiry(""); setAddSearch("");
      setAddStep("search"); setAddSuggestions([]);
    } catch (err) { console.error(err); }
    setAddingNew(false);
  };

  const openAddModal = () => {
    setShowAddModal(true);
    setAddStep("search");
    setAddCategory("other");
    setAddName("");
    setAddQty("");
    setAddUnit("шт");
    setAddExpiry("");
    setAddSearch("");
    setAddSuggestions([]);
  };

  // Dynamic search from products.json database
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (showDetail) { setShowDetail(null); return; }
      if (showAddModal) { setShowAddModal(false); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showDetail, showAddModal]);

  useEffect(() => {
    if (!showAddModal || addStep !== "search") return;

    const searchProducts = async () => {
      if (addSearch.trim().length < 2) {
        // Show popular products when no search
        try {
          const res = await api().get("/products/search?query=&limit=20");
          setAddSuggestions(res.data?.results || []);
        } catch (err) {
          setAddSuggestions([]);
        }
        return;
      }

      try {
        const res = await api().get(`/products/search?query=${encodeURIComponent(addSearch.trim())}&limit=15`);
        setAddSuggestions(res.data?.results || []);
      } catch (err) {
        setAddSuggestions([]);
      }
    };

    const debounce = setTimeout(searchProducts, 200);
    return () => clearTimeout(debounce);
  }, [addSearch, showAddModal, addStep, api]);

  const selectSuggestion = (name, category) => {
    setAddName(name);
    setAddCategory(category);
    setAddStep("details");
  };

  const selectCustomName = () => {
    setAddName(addSearch.trim());
    setAddStep("details");
  };

  // addSuggestions already filtered from API, just use directly

  const ItemCard = ({ item }) => {
    const days = daysUntilExpiry(item.expiry_date);
    const isExpiring = days !== null && days < 4;
    const isExpired = days !== null && days < 0;
    const isSelected = selectMode && selectedIds.includes(item.id);
    return (
      <button
        data-testid={`fridge-item-${item.id}`}
        onClick={() => (selectMode ? toggleSelect(item.id) : openDetail(item))}
        className={`relative bg-white rounded-2xl p-3 border text-left flex flex-col items-center gap-1.5 hover:shadow-md transition-all active:scale-[0.97] ${
          isSelected
            ? "border-[#7B9E7F] ring-2 ring-[#7B9E7F]/50 bg-[#7B9E7F]/5"
            : isExpired ? "border-[#BC4749]/30 bg-[#BC4749]/5" : isExpiring ? "border-[#E9C46A]/40" : "border-black/5"
        } shadow-[0_2px_8px_rgb(0,0,0,0.04)]`}
      >
        {selectMode && (
          <span className="absolute top-1.5 right-1.5">
            {isSelected
              ? <CheckCircle2 className="w-5 h-5 text-[#7B9E7F]" strokeWidth={2} />
              : <Circle className="w-5 h-5 text-[#C7C7BD]" strokeWidth={1.5} />}
          </span>
        )}
        <span className="text-3xl">{item.icon || CAT_ICONS[item.category] || "📦"}</span>
        <p className="font-body font-semibold text-[#3D4035] text-xs text-center leading-tight truncate w-full">{item.localized_name || item.name}</p>
        {item.quantity && (
          <p className="font-body text-[10px] text-[#A5A58D]">{formatAmount(item.quantity, lang)} {formatUnitPlural(item.unit, item.quantity, lang)}</p>
        )}
        {item.expiry_date && (
          <p className={`font-body text-[10px] font-bold ${isExpired || isExpiring ? "text-[#BC4749]" : "text-[#A5A58D]"}`}>
            {isExpired ? t("expired") : days === 0 ? t("today") : `${days} ${t("daysLeft")}`}
          </p>
        )}
      </button>
    );
  };

  return (
    <div className="min-h-screen bg-[#FAFAF8] pb-32" data-testid="fridge-page">
      {/* Header */}
      <div className="pl-[72px] pr-5 pt-6 pb-3 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-heading text-3xl text-[#3D4035]">{t("fridgeTitle")}</h1>
          <p className="font-body text-sm text-[#A5A58D] mt-0.5">{items.length} {plural(lang, items.length, { one: t("fridgeItemCount_one"), few: t("fridgeItemCount_few"), many: t("fridgeItemCount") })}</p>
        </div>
        {items.length > 0 && (
          <div className="shrink-0 self-start flex items-center gap-2">
            {selectMode ? (
              <button
                data-testid="fridge-select-cancel"
                onClick={exitSelectMode}
                className="h-9 px-3 rounded-full bg-white border border-[#E8E5E0] text-[#6B705C] font-body text-sm font-semibold flex items-center gap-1.5 hover:border-[#7B9E7F]/40 transition-all"
              >
                <X className="w-4 h-4" strokeWidth={1.5} /> {t("cancel")}
              </button>
            ) : (
              <>
                <button
                  data-testid="fridge-select-toggle"
                  onClick={enterSelectMode}
                  className="h-9 px-3 rounded-full bg-white border border-[#E8E5E0] text-[#6B705C] font-body text-sm font-semibold flex items-center gap-1.5 hover:border-[#7B9E7F]/40 transition-all"
                >
                  <ListChecks className="w-4 h-4" strokeWidth={1.5} /> {t("fridgeSelect")}
                </button>
                <button
                  data-testid="clear-fridge-btn"
                  onClick={() => setShowClearConfirm(true)}
                  aria-label={t("fridgeClear")}
                  className="w-9 h-9 rounded-full bg-[#BC4749]/10 text-[#BC4749] flex items-center justify-center hover:bg-[#BC4749]/20 transition-colors"
                >
                  <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Category Filter Tabs */}
      <div className="px-5 mb-3 overflow-x-auto scrollbar-hide" data-testid="category-tabs">
        <div className="flex gap-2 pb-1">
          <button
            data-testid="cat-tab-all"
            onClick={() => setActiveCat("all")}
            className={`shrink-0 px-4 h-9 rounded-full font-body text-sm font-semibold transition-all ${
              activeCat === "all"
                ? "bg-[#7B9E7F] text-white shadow-md shadow-[#7B9E7F]/20"
                : "bg-white text-[#6B705C] border border-[#E8E5E0] hover:border-[#7B9E7F]/40"
            }`}
          >
            {t("allCategories")}
          </button>
          {CATEGORIES.map((cat) => {
            const count = grouped[cat]?.length || 0;
            if (count === 0) return null;
            return (
              <button
                key={cat}
                data-testid={`cat-tab-${cat}`}
                onClick={() => setActiveCat(activeCat === cat ? "all" : cat)}
                className={`shrink-0 px-3 h-9 rounded-full font-body text-sm font-semibold transition-all flex items-center gap-1.5 ${
                  activeCat === cat
                    ? "bg-[#7B9E7F] text-white shadow-md shadow-[#7B9E7F]/20"
                    : "bg-white text-[#6B705C] border border-[#E8E5E0] hover:border-[#7B9E7F]/40"
                }`}
              >
                <span className="text-base">{CAT_ICONS[cat]}</span>
                {t(`cat_${cat}`)}
                <span className={`text-xs rounded-full px-1.5 py-0.5 ${
                  activeCat === cat ? "bg-white/20" : "bg-[#EBE9E4]"
                }`}>{count}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="px-5">
        {/* Expiry Alert Banner */}
        {expiringItems.length > 0 && (
          <button data-testid="expiry-alert-banner" onClick={() => { setExpiryFilter(!expiryFilter); setActiveCat("all"); }}
            className="w-full mb-4 bg-[#BC4749]/10 rounded-2xl p-4 border border-[#BC4749]/20 text-left hover:bg-[#BC4749]/15 transition-colors">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-6 h-6 text-[#BC4749] shrink-0" strokeWidth={1.5} />
              <div className="flex-1">
                <p className="font-body font-bold text-[#BC4749] text-sm">{t("expiryAlertTitle")}</p>
                <p className="font-body text-xs text-[#6B705C] mt-0.5">{t("expiryAlertMsg")}</p>
                {expiredItems.length > 0 && (
                  <p className="font-body text-xs font-semibold text-[#BC4749] mt-1">
                    {t("expiredNotUsed")} ({expiredItems.length})
                  </p>
                )}
              </div>
              <ChevronRight className={`w-5 h-5 text-[#BC4749] transition-transform ${expiryFilter ? "rotate-90" : ""}`} strokeWidth={1.5} />
            </div>
            {expiryFilter && (
              <p className="font-body text-xs text-[#BC4749] mt-2 pl-9">{t("showingExpiring")} ({expiringItems.length})</p>
            )}
          </button>
        )}

        {/* Pending Items Banner */}
        {hasPending && (
          <button data-testid="pending-fridge-banner" onClick={() => setShowPending(true)}
            className="w-full mb-4 bg-[#7B9E7F]/10 rounded-2xl p-4 border border-[#7B9E7F]/20 text-left hover:bg-[#7B9E7F]/15 transition-colors">
            <div className="flex items-center gap-3">
              <Package className="w-6 h-6 text-[#7B9E7F] shrink-0" strokeWidth={1.5} />
              <div className="flex-1">
                <p className="font-body font-bold text-[#4A6B4E]">{t("newProducts")}</p>
                <p className="font-body text-sm text-[#6B705C] mt-0.5">{plural(lang, pendingItems.length, { one: t("newProductsMsg_one"), few: t("newProductsMsg_few"), many: t("newProductsMsg") }).replace("{n}", pendingItems.length)}</p>
              </div>
              <ChevronRight className="w-5 h-5 text-[#7B9E7F]" strokeWidth={1.5} />
            </div>
          </button>
        )}

        {/* Tutorial Step 7 - Pending items banner when arriving at fridge */}
        {hasPending && tutorialActive && tutorialStep === 7 && (
          <TutorialTooltip step={7} targetId="pending-fridge-banner" />
        )}

        {/* Tutorial Step 8 - Go to Home (shows after confirming pending items) */}
        {showGoHomeStep && tutorialActive && tutorialStep === 8 && (
          <TutorialTooltip step={8} targetId="nav-home" />
        )}

        {expiryFilter && (
          <button onClick={() => setExpiryFilter(false)} className="mb-3 font-body text-sm text-[#7B9E7F] flex items-center gap-1">
            <X className="w-4 h-4" strokeWidth={1.5} /> {t("showAll")}
          </button>
        )}

        {/* Fridge Items — Categorized Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 text-[#7B9E7F] animate-spin" /></div>
        ) : displayCats.length === 0 ? (
          <div className="text-center py-16">
            <Refrigerator className="w-12 h-12 text-[#A5A58D] mx-auto mb-3" strokeWidth={1.5} />
            <p className="font-body text-[#6B705C]">{expiryFilter ? t("noExpiringItems") : t("emptyFridge")}</p>
          </div>
        ) : (
          <div className="space-y-5">
            {displayCats.map((cat) => (
              <section key={cat} data-testid={`fridge-section-${cat}`}>
                <div className="flex items-center gap-2 mb-2.5">
                  <span className="text-lg">{CAT_ICONS[cat]}</span>
                  <h2 className="font-body font-bold text-[#3D4035] text-sm">{t(`cat_${cat}`)}</h2>
                  <span className="font-body text-xs text-[#A5A58D]">({grouped[cat].length})</span>
                </div>
                <div className="grid grid-cols-3 gap-2.5">
                  {grouped[cat].map((item) => (
                    <ItemCard key={item.id} item={item} />
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>

      {/* Floating Add Button - Bottom Right, above nav (hidden in select mode) */}
      {!selectMode && (
        <button
          data-testid="add-fridge-item"
          onClick={openAddModal}
          className="fixed bottom-24 right-5 w-14 h-14 rounded-full bg-[#7B9E7F] text-white flex items-center justify-center shadow-lg shadow-[#7B9E7F]/30 hover:bg-[#6B8E6F] transition-all z-50 active:scale-95"
        >
          <Plus className="w-6 h-6" strokeWidth={2} />
        </button>
      )}

      {/* Bulk selection action bar */}
      {selectMode && (
        <div data-testid="fridge-bulk-bar" className="fixed bottom-24 inset-x-0 z-50 px-5">
          <div className="w-full max-w-[480px] mx-auto bg-white rounded-2xl border border-[#E8E5E0] shadow-[0_4px_20px_rgb(0,0,0,0.10)] p-3 flex items-center gap-3">
            <button
              data-testid="fridge-select-all"
              onClick={toggleSelectAll}
              className="shrink-0 h-11 px-3 rounded-xl bg-[#FAFAF8] border border-[#E8E5E0] text-[#6B705C] font-body text-sm font-semibold hover:border-[#7B9E7F]/40 transition-all"
            >
              {allVisibleSelected ? t("fridgeDeselectAll") : t("fridgeSelectAll")}
            </button>
            <button
              data-testid="fridge-delete-selected"
              onClick={handleBulkDelete}
              disabled={selectedIds.length === 0 || bulkDeleting}
              className="flex-1 h-11 rounded-xl bg-[#BC4749] text-white font-body font-bold text-sm flex items-center justify-center gap-2 hover:bg-[#A93E40] transition-all disabled:opacity-40"
            >
              {bulkDeleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" strokeWidth={1.5} />}
              {t("fridgeDeleteSelected").replace("{n}", selectedIds.length)}
            </button>
          </div>
        </div>
      )}

      {/* Clear Fridge Confirmation Modal */}
      {showClearConfirm && (
        <div className="fixed inset-0 z-[10001] bg-black/40 flex items-end" data-testid="clear-fridge-modal">
          <div className="w-full max-w-[480px] mx-auto bg-[#FAFAF8] rounded-t-3xl p-6">
            <div className="flex items-start gap-3 mb-5">
              <div className="w-11 h-11 rounded-full bg-[#BC4749]/10 flex items-center justify-center shrink-0">
                <AlertTriangle className="w-5 h-5 text-[#BC4749]" strokeWidth={1.5} />
              </div>
              <div className="flex-1">
                <h3 className="font-heading text-xl text-[#3D4035]">{t("fridgeClearTitle")}</h3>
                <p className="font-body text-sm text-[#6B705C] mt-1">{t("fridgeClearMsg").replace("{n}", items.length)}</p>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowClearConfirm(false)}
                disabled={clearing}
                className="flex-1 h-12 rounded-xl bg-white border border-[#E8E5E0] text-[#6B705C] font-body font-bold hover:bg-[#F2E9E4] transition-all disabled:opacity-50"
              >
                {t("cancel")}
              </button>
              <button
                data-testid="clear-fridge-confirm"
                onClick={handleClearAll}
                disabled={clearing}
                className="flex-1 h-12 rounded-xl bg-[#BC4749] text-white font-body font-bold flex items-center justify-center gap-2 hover:bg-[#A93E40] transition-all disabled:opacity-50"
              >
                {clearing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" strokeWidth={1.5} />}
                {t("fridgeClearConfirm")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pending Review Modal */}
      {showPending && (
        <div className="fixed inset-0 z-[10001] bg-black/40 flex items-end" data-testid="pending-modal">
          <div className="w-full max-w-[480px] mx-auto bg-[#FAFAF8] rounded-t-3xl max-h-[85vh] flex flex-col">
            <div className="px-6 pt-6 pb-4 flex items-center justify-between shrink-0 border-b border-[#EBE9E4]">
              <div>
                <h3 className="font-heading text-xl text-[#3D4035]">{t("newProducts")}</h3>
                <p className="font-body text-sm text-[#A5A58D] mt-0.5">{t("setPendingExpiry")}</p>
              </div>
              <button onClick={() => setShowPending(false)} className="w-9 h-9 rounded-full bg-[#EBE9E4] flex items-center justify-center">
                <X className="w-4 h-4 text-[#6B705C]" strokeWidth={1.5} />
              </button>
            </div>

            {isPremium && (
              <div className="px-6 py-2 bg-[#E9C46A]/10 flex items-center gap-2 shrink-0">
                <Crown className="w-4 h-4 text-[#8B6914]" strokeWidth={1.5} />
                <span className="font-body text-xs text-[#8B6914] font-semibold">{t("autoExpiryPremium")}</span>
              </div>
            )}

            <div className="flex-1 overflow-y-auto p-6 space-y-3">
              {pendingItems.map((item) => (
                <div key={item.id} data-testid={`pending-item-${item.id}`}
                  className="bg-white rounded-2xl p-4 border border-black/5">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-xl">{item.icon || "📦"}</span>
                    <div className="flex-1 min-w-0">
                      <p className="font-body font-semibold text-[#3D4035] truncate">{item.localized_name || item.name}</p>
                      {item.amount && <p className="font-body text-xs text-[#A5A58D]">{formatAmount(item.amount, lang)} {formatUnitPlural(item.unit, item.amount, lang)}</p>}
                    </div>
                    {item.shelf_days && (
                      <span className="font-body text-xs text-[#7B9E7F] bg-[#7B9E7F]/10 px-2 py-0.5 rounded-full flex items-center gap-1">
                        <Crown className="w-3 h-3" strokeWidth={1.5} /> ~{item.shelf_days}d
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-[#A5A58D] shrink-0" strokeWidth={1.5} />
                    <input
                      data-testid={`pending-expiry-${item.id}`}
                      type="date"
                      value={item.expiry_date || item.suggested_expiry || ""}
                      onChange={(e) => updatePendingExpiry(item.id, e.target.value)}
                      className="flex-1 bg-[#FAFAF8] ring-1 ring-[#E8E5E0] focus:ring-2 focus:ring-[#7B9E7F]/50 rounded-xl h-10 px-3 font-body text-sm text-[#3D4035] outline-none"
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="px-6 pb-6 pt-3 border-t border-[#EBE9E4] shrink-0">
              <button data-testid="confirm-pending-btn" onClick={confirmPending} disabled={confirmingPending}
                className="w-full bg-[#7B9E7F] text-white rounded-full h-14 font-body font-bold text-base hover:bg-[#6B8E6F] transition-all disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg shadow-[#7B9E7F]/20">
                {confirmingPending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Refrigerator className="w-5 h-5" strokeWidth={1.5} />}
                {t("addToFridge")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Item Detail Modal */}
      {showDetail && (
        <div className="fixed inset-0 z-[10001] bg-black/40 flex items-end" data-testid="fridge-detail-modal">
          <div className="w-full max-w-[480px] mx-auto bg-[#FAFAF8] rounded-t-3xl p-6">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <span className="text-3xl">{showDetail.icon || CAT_ICONS[showDetail.category] || "📦"}</span>
                <h3 className="font-heading text-xl text-[#3D4035]">{showDetail.localized_name || showDetail.name}</h3>
              </div>
              <button onClick={() => setShowDetail(null)} className="w-9 h-9 rounded-full bg-[#EBE9E4] flex items-center justify-center">
                <X className="w-4 h-4 text-[#6B705C]" strokeWidth={1.5} />
              </button>
            </div>

            <div className="space-y-3 mb-5">
              <div className="flex gap-2">
                <input data-testid="detail-quantity" type="text" value={editData.quantity || ""}
                  onChange={(e) => setEditData({ ...editData, quantity: e.target.value })}
                  placeholder={t("amountPlaceholder")}
                  className="flex-1 bg-white ring-1 ring-[#E8E5E0] focus:ring-2 focus:ring-[#7B9E7F]/50 rounded-xl h-12 px-4 font-body text-[#3D4035] outline-none" />
                <div className="flex bg-white ring-1 ring-[#E8E5E0] rounded-xl overflow-hidden">
                  {["шт", "кг", "г", "л"].map((u) => (
                    <button key={u} onClick={() => setEditData({ ...editData, unit: u })}
                      className={`px-3 h-12 font-body text-sm font-semibold transition-colors ${
                        editData.unit === u ? "bg-[#7B9E7F] text-white" : "text-[#6B705C] hover:bg-[#F2E9E4]"
                      }`}>{formatUnit(u, lang)}</button>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-[#A5A58D] shrink-0" strokeWidth={1.5} />
                <input data-testid="detail-expiry" type="date" value={editData.expiry_date || ""}
                  onChange={(e) => setEditData({ ...editData, expiry_date: e.target.value })}
                  className="flex-1 bg-white ring-1 ring-[#E8E5E0] focus:ring-2 focus:ring-[#7B9E7F]/50 rounded-xl h-12 px-4 font-body text-sm text-[#3D4035] outline-none" />
              </div>
              <select value={editData.category} onChange={(e) => setEditData({ ...editData, category: e.target.value })}
                className="w-full bg-white ring-1 ring-[#E8E5E0] focus:ring-2 focus:ring-[#7B9E7F]/50 rounded-xl h-12 px-4 font-body text-[#3D4035] outline-none">
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>{`${CAT_ICONS[c]} ${t(`cat_${c}`)}`}</option>
                ))}
              </select>
            </div>

            <div className="flex gap-3">
              <button data-testid="detail-delete" onClick={() => handleDelete(showDetail.id)} disabled={deletingId === showDetail.id}
                className="w-14 h-12 rounded-xl bg-[#BC4749]/10 text-[#BC4749] flex items-center justify-center hover:bg-[#BC4749]/20 transition-colors disabled:opacity-50">
                {deletingId === showDetail.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-5 h-5" strokeWidth={1.5} />}
              </button>
              <button data-testid="detail-save" onClick={saveDetail} disabled={saving}
                className="flex-1 bg-[#7B9E7F] text-white rounded-xl h-12 font-body font-bold hover:bg-[#6B8E6F] transition-all disabled:opacity-50 flex items-center justify-center gap-2">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-5 h-5" strokeWidth={1.5} />}
                {t("save")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add New Item Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-[10001] bg-black/40 flex items-end" data-testid="add-fridge-modal">
          <div className="w-full max-w-[480px] mx-auto bg-[#FAFAF8] rounded-t-3xl max-h-[80vh] flex flex-col">
            <div className="px-6 pt-6 pb-4 flex items-center justify-between shrink-0">
              <h3 className="font-heading text-xl text-[#3D4035]">{t("addFridgeItem")}</h3>
              <button onClick={() => setShowAddModal(false)} className="w-9 h-9 rounded-full bg-[#EBE9E4] flex items-center justify-center">
                <X className="w-4 h-4 text-[#6B705C]" strokeWidth={1.5} />
              </button>
            </div>

            {addStep === "search" ? (
              <div className="flex flex-col flex-1 min-h-0 px-6 pb-6">
                {/* Suggestions container - placed ABOVE input to avoid keyboard */}
                <div className="overflow-y-auto flex-1 -mx-1 px-1 mb-3 flex flex-col-reverse">
                  <div className="bg-white rounded-xl border border-black/5 divide-y divide-[#F2E9E4]">
                    {addSuggestions.slice(0, 15).map((s, idx) => (
                      <button key={idx} data-testid={`fridge-suggestion-${idx}`}
                        onClick={() => selectSuggestion(s.name, s.category)}
                        className="w-full px-4 py-3 text-left font-body text-[#3D4035] hover:bg-[#FAFAF8] transition-colors flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-base">{CAT_ICONS[s.category] || "📦"}</span>
                          <span>{s.name}</span>
                        </div>
                        <span className="font-body text-xs text-[#A5A58D]">{t(`cat_${s.category}`)}</span>
                      </button>
                    ))}
                    {addSuggestions.length === 0 && !addSearch.trim() && (
                      <p className="px-4 py-3 font-body text-sm text-[#A5A58D]">{t("itemName")}</p>
                    )}
                  </div>
                  {addSearch.trim() && (
                    <button data-testid="fridge-add-custom"
                      onClick={selectCustomName}
                      className="w-full mt-2 px-4 py-3 bg-[#7B9E7F] text-white rounded-xl font-body font-semibold flex items-center gap-2 hover:bg-[#6B8E6F] transition-all">
                      <Plus className="w-4 h-4" strokeWidth={2} />
                      {t("addItem") || "Додати"}: "{addSearch.trim()}"
                    </button>
                  )}
                </div>
                {/* Input at the bottom - visible above mobile keyboard */}
                <div className="relative shrink-0">
                  <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A5A58D]" strokeWidth={1.5} />
                  <input data-testid="fridge-add-search"
                    type="text" value={addSearch}
                    onChange={(e) => setAddSearch(e.target.value)}
                    placeholder={t("searchOrType") || t("itemName")}
                    className="w-full bg-white ring-1 ring-[#E8E5E0] focus:ring-2 focus:ring-[#7B9E7F]/50 rounded-xl h-12 pl-10 pr-4 font-body text-[#3D4035] outline-none"
                    autoFocus
                  />
                </div>
              </div>
            ) : (
              <div className="px-6 pb-6">
                <button onClick={() => setAddStep("search")}
                  className="font-body text-sm text-[#7B9E7F] mb-3 flex items-center gap-1">
                  <ChevronRight className="w-4 h-4 rotate-180" strokeWidth={1.5} /> {t("back") || "Назад"}
                </button>
                <div className="flex items-center gap-2 mb-4 bg-white rounded-xl p-3 border border-black/5">
                  <span className="text-xl">{CAT_ICONS[addCategory] || "📦"}</span>
                  <span className="font-body font-bold text-[#3D4035]">{addName}</span>
                </div>
                <div className="space-y-3 mb-5">
                  <div className="flex gap-2">
                    <input type="text" inputMode="numeric" value={addQty}
                      onChange={(e) => setAddQty(e.target.value)} placeholder={t("amountPlaceholder")}
                      className="flex-1 bg-white ring-1 ring-[#E8E5E0] focus:ring-2 focus:ring-[#7B9E7F]/50 rounded-xl h-12 px-4 font-body text-[#3D4035] outline-none" autoFocus />
                    <div className="flex bg-white ring-1 ring-[#E8E5E0] rounded-xl overflow-hidden">
                      {["шт", "кг", "г", "л"].map((u) => (
                        <button key={u} onClick={() => setAddUnit(u)}
                          className={`px-3 h-12 font-body text-sm font-semibold transition-colors ${
                            addUnit === u ? "bg-[#7B9E7F] text-white" : "text-[#6B705C] hover:bg-[#F2E9E4]"
                          }`}>{formatUnit(u, lang)}</button>
                      ))}
                    </div>
                  </div>
                  {addQty.trim() === "" && (
                    <div data-testid="qty-default-hint" className="flex items-center gap-2 bg-[#E9C46A]/10 border border-[#E9C46A]/30 rounded-xl px-3 py-2">
                      <AlertTriangle className="w-4 h-4 text-[#8B6914] shrink-0" strokeWidth={1.5} />
                      <p className="font-body text-xs text-[#8B6914]">{t("qtyDefaultHint")}</p>
                    </div>
                  )}
                  <select value={addCategory} onChange={(e) => setAddCategory(e.target.value)}
                    className="w-full bg-white ring-1 ring-[#E8E5E0] rounded-xl h-12 px-4 font-body text-[#3D4035] outline-none">
                    {CATEGORIES.map((c) => (
                      <option key={c} value={c}>{`${CAT_ICONS[c]} ${t(`cat_${c}`)}`}</option>
                    ))}
                  </select>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-[#A5A58D] shrink-0" strokeWidth={1.5} />
                    <input type="date" value={addExpiry} onChange={(e) => setAddExpiry(e.target.value)}
                      className="flex-1 bg-white ring-1 ring-[#E8E5E0] rounded-xl h-12 px-4 font-body text-sm text-[#3D4035] outline-none" />
                  </div>
                </div>
                <button data-testid="save-new-fridge" onClick={handleAddItem} disabled={!addName.trim() || addingNew}
                  className="w-full bg-[#7B9E7F] text-white rounded-full h-14 font-body font-bold hover:bg-[#6B8E6F] transition-all disabled:opacity-50 flex items-center justify-center gap-2">
                  {addingNew && <Loader2 className="w-4 h-4 animate-spin" />}
                  {t("addToFridge")}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
